import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBase } from '../../../_classes/form-base.class';
import { firstValueFrom, Observable, of } from 'rxjs';
import { SessionService } from '../../../_services/session/session.service';
import { MessageService } from 'primeng/api';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ProductsService } from '../../../_services/products/products.service';
import { ProductImageComponent } from '../../product-image/product-image.component';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { CommonModule } from '@angular/common';
import { ProductsCategoriesService } from '../../../_services/products/products-categories.service';
import { ValidatorService } from '../../../_services/validators/validator.service';
import { InputTextModule } from 'primeng/inputtext';
import { EResources } from '../../../_resources/EResources';
import { InputNumberModule } from 'primeng/inputnumber';
import IProductCategory from '../../../_interfaces/IProductCategory';
import IProduct from '../../../_interfaces/IProduct';
import { AdminFormActionsComponent } from '../../admin-ui/admin-form-actions/admin-form-actions.component';
import { RadioButtonModule } from 'primeng/radiobutton';
import { catchError, map, switchMap } from 'rxjs/operators';
import { RouterLink } from '@angular/router';
import { CurrenciesService } from '../../../_services/currencies/currencies.service';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-form-products',
  imports: [
    ProductImageComponent,
    TranslatePipe,
    FormsModule,
    ButtonModule,
    SelectModule,
    CommonModule,
    InputTextModule,
    InputNumberModule,
    AdminFormActionsComponent,
    RadioButtonModule,
    RouterLink,
  ],
  templateUrl: './form-products.component.html',
  styleUrl: './form-products.component.scss',
})
export class FormProductsComponent
  extends FormBase<IProduct>
  implements OnInit, OnDestroy
{
  @Input() showIntro = true;
  public saveMode: 'manual' | 'all-clinics' = 'manual';
  public readonly colorPresets = [
    '#2563eb',
    '#14b8a6',
    '#22c55e',
    '#f59e0b',
    '#ef4444',
    '#8b5cf6',
    '#ec4899',
    '#334155',
  ];
  public productsCategories: IProductCategory[];
  public defaultProductImg = 'assets/images/default/product.png';
  public submitted = false;
  public submitting = false;
  public categoriesLoaded = false;
  public basePriceCurrencyLabel = '';
  public selectedImageName = '';
  public imageUploadFailed = false;
  private currencySymbolByCode = new Map<string, string>();
  private selectedImageFile?: File;
  private imagePreviewObjectUrl?: string;

  get hasCategories(): boolean {
    return (this.productsCategories?.length || 0) > 0;
  }

  get showMissingCategoryWarning(): boolean {
    return this.mode === 'create' && this.categoriesLoaded && !this.hasCategories;
  }

  constructor(
    public session: SessionService,
    public messageService: MessageService,
    public translate: TranslateService,
    public productService: ProductsService,
    public productsCategoriesService: ProductsCategoriesService,
    public validator: ValidatorService,
    private currenciesService: CurrenciesService,
  ) {
    super(session, messageService, translate);
  }

  clear(): void {
    this.releaseImagePreviewObjectUrl();
    this.saveMode = 'manual';
    this.selectedImageFile = undefined;
    this.selectedImageName = '';
    this.imageUploadFailed = false;
    this.payload = {
      image_url: undefined,
      products_categories_id: undefined,
      name: undefined,
      description: undefined,
      color: '#3b82f6',
      basePrice: 0,
    };
  }
  generatePayload(): IProduct {
    return {
      image_url: this.payload.image_url,
      products_categories_id: this.payload.products_categories_id,
      name: this.payload.name,
      description: this.payload.description,
      color: this.payload.color,
      basePrice: Number(this.payload.basePrice),
    };
  }
  getResourceName(): string {
    return EResources.PRODUCTS;
  }
  updateFn(payload: IProduct): Observable<any> {
    return this.productService.update(this.customers_id, this.id, payload);
  }
  createFn(payload: IProduct): Observable<any> {
    return this.productService.create(
      this.customers_id,
      payload.products_categories_id,
      payload
    );
  }
  properties(): string[] {
    return ['name', 'description', 'color', 'products_categories_id'];
  }
  validate(): Promise<void> {
    return this.validator.product(this.payload);
  }

  loadCategories() {
    this.productsCategoriesService.getByCustomer(this.customers_id).subscribe({
      next: (products_categories: IProductCategory[]) => {
        this.productsCategories = products_categories || [];
        this.categoriesLoaded = true;
        if (this.mode === 'create' && this.hasCategories) {
          this.payload.products_categories_id = products_categories[0].id;
        }
      },
      error: () => {
        this.productsCategories = [];
        this.categoriesLoaded = true;
      },
    });
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.saveMode = 'manual';
    this.payload.color = this.normalizeColor(this.payload.color || '#3b82f6');
    if (this.payload.basePrice === undefined || this.payload.basePrice === null) {
      const firstBasePrice = this.payload.basePrices?.[0]?.price;
      this.payload.basePrice = Number(firstBasePrice ?? 0);
    }
    this.loadBasePriceCurrencyLabel();
    this.loadCategories();
  }

  ngOnDestroy(): void {
    this.releaseImagePreviewObjectUrl();
  }

  private loadBasePriceCurrencyLabel(): void {
    this.currenciesService.getAll().subscribe({
      next: (currencies) => {
        this.currencySymbolByCode.clear();
        const currencyCodeById = new Map<number, string>();
        (currencies || []).forEach((currency) => {
          const code = String(currency?.code || '').trim().toUpperCase();
          const symbol = String(currency?.symbol || '').trim();
          if (code && symbol) this.currencySymbolByCode.set(code, symbol);
          if (currency?.id && code) {
            currencyCodeById.set(+currency.id, code);
          }
        });

        const rawCustomerCurrency = String(this.customer?.currency || '')
          .trim()
          .toUpperCase();
        const customerCurrencyId = Number((this.customer as any)?.currencies_id || 0);
        const resolvedCode =
          rawCustomerCurrency || currencyCodeById.get(customerCurrencyId) || '';

        this.basePriceCurrencyLabel =
          this.currencySymbolByCode.get(resolvedCode) || resolvedCode;
      },
      error: () => {
        this.basePriceCurrencyLabel = String(
          this.customer?.currency || '',
        )
          .trim()
          .toUpperCase();
      },
    });
  }

  override async execute(): Promise<void> {
    if (this.showMissingCategoryWarning) {
      this.submitting = false;
      return;
    }

    this.submitted = true;
    this.submitting = true;
    await super.execute();
    const hasValidationErrors = Object.values(this.errors || {}).some((value) => !!value);
    if (hasValidationErrors) {
      this.submitting = false;
    }
  }

  override update(payload: IProduct): void {
    this.updateFn(payload).subscribe({
      next: async () => {
        this.imageUploadFailed = false;
        const imageUploadResult = await this.uploadSelectedImageIfNeeded(this.id);
        const updatedPayload = {
          ...payload,
          image_url: imageUploadResult.imageUrl || this.payload.image_url || payload.image_url,
        };
        if (this.clearOnUpdate) {
          this.clear();
        }
        this.updateMessageSuccess();
        this.showImageUploadResultMessage(imageUploadResult);
        this.onUpdate(updatedPayload);
        this.submitting = false;
      },
      error: () => {
        this.updateMessageError();
        this.submitting = false;
      },
    });
  }

  override create(payload: IProduct): void {
    this.createFn(payload)
      .pipe(
        switchMap(async (created) => {
          const product = this.resolveCreatedProduct(created, payload);
          const imageUploadResult = await this.uploadSelectedImageIfNeeded(product?.id);
          if (imageUploadResult.imageUrl) {
            product.image_url = imageUploadResult.imageUrl;
          }
          if (this.saveMode !== 'all-clinics') {
            return of({
              product,
              imageUploadResult,
              assignment: null,
            });
          }
          if (!product?.id) {
            return of({
              product,
              imageUploadResult,
              assignment: null,
            });
          }
          return this.assignToAllClinics(product).pipe(
            map((assignment) => ({
              product,
              imageUploadResult,
              assignment,
            })),
          );
        }),
        switchMap((result$) => result$),
      )
      .subscribe({
        next: ({ product, imageUploadResult, assignment }) => {
          this.clear();
          this.createMessageSuccess();
          this.showImageUploadResultMessage(imageUploadResult);
          this.showAssignmentMessage(assignment);
          this.onCreate(product);
          this.submitting = false;
        },
        error: () => {
          this.createMessageError();
          this.submitting = false;
        },
      });
  }

  onColorInput(rawValue: unknown): void {
    this.payload.color = this.normalizeColor(rawValue);
  }

  get colorPreview(): string {
    return this.normalizeColor(this.payload.color || '#3b82f6');
  }

  get formTitleKey(): string {
    return this.mode === 'edit' ? 'COMMON.PRODUCTS' : 'ADMIN.PRODUCTS.ADD';
  }

  get formDescriptionKey(): string {
    return 'ADMIN.IA.PRODUCTS.DESCRIPTION';
  }

  selectColorPreset(color: string): void {
    this.onColorInput(color);
  }

  get imagePreviewUrl(): string {
    return this.imagePreviewObjectUrl || this.payload.image_url || this.defaultProductImg;
  }

  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0];

    this.selectedImageFile = file || undefined;
    this.selectedImageName = file?.name || '';
    this.imageUploadFailed = false;
    this.releaseImagePreviewObjectUrl();

    if (file) {
      this.imagePreviewObjectUrl = URL.createObjectURL(file);
    }
  }

  private async uploadSelectedImageIfNeeded(productId?: number): Promise<{
    uploaded: boolean;
    failed: boolean;
    imageUrl?: string;
    error?: HttpErrorResponse | unknown;
  }> {
    if (!this.selectedImageFile || !productId) {
      return { uploaded: false, failed: false };
    }

    try {
      const response = await firstValueFrom(
        this.productService.uploadImage(
          this.customers_id,
          productId,
          this.selectedImageFile,
        ),
      );

      const imageUrl = `${response?.image_url || ''}`.trim();
      if (imageUrl) {
        this.payload.image_url = imageUrl;
      }

      this.releaseImagePreviewObjectUrl();
      this.selectedImageFile = undefined;
      this.selectedImageName = '';

      return {
        uploaded: true,
        failed: false,
        imageUrl,
      };
    } catch (error) {
      this.imageUploadFailed = true;
      return {
        uploaded: false,
        failed: true,
        error,
      };
    }
  }

  private showImageUploadResultMessage(result: {
    uploaded: boolean;
    failed: boolean;
    error?: HttpErrorResponse | unknown;
  }): void {
    if (!result?.failed) {
      return;
    }

    this.messageService.add({
      severity: 'warn',
      summary: this.translate.instant('ADMIN.FORMS.PRODUCT.IMAGE_UPLOAD_PARTIAL_SUMMARY'),
      detail: this.translate.instant('ADMIN.FORMS.PRODUCT.IMAGE_UPLOAD_PARTIAL_DETAIL'),
    });
  }

  private releaseImagePreviewObjectUrl(): void {
    if (this.imagePreviewObjectUrl) {
      URL.revokeObjectURL(this.imagePreviewObjectUrl);
      this.imagePreviewObjectUrl = undefined;
    }
  }

  private resolveCreatedProduct(created: unknown, fallbackPayload: IProduct): IProduct {
    if (created && typeof created === 'object') {
      const createdObject = created as Record<string, any>;
      const directProduct = createdObject as IProduct;
      if (directProduct.id) {
        return {
          ...fallbackPayload,
          ...directProduct,
        };
      }

      if (createdObject['product']?.id) {
        return {
          ...fallbackPayload,
          ...createdObject['product'],
        };
      }
    }

    return fallbackPayload;
  }

  private assignToAllClinics(product: IProduct): Observable<{
    total: number;
    success: number;
    failed: number;
  }> {
    const productsId = +(product?.id || 0);
    if (!productsId) {
      return of({ total: 0, success: 0, failed: 0 });
    }

    return this.productService.assignToAllSites(this.customers_id, productsId).pipe(
      map((result) => ({
        total: +(result?.total || 0),
        success: +(result?.success || 0),
        failed: +(result?.failed || 0),
      })),
      catchError(() => of({ total: 1, success: 0, failed: 1 })),
    );
  }

  private showAssignmentMessage(assignment: {
    total: number;
    success: number;
    failed: number;
  } | null): void {
    if (this.saveMode !== 'all-clinics' || !assignment) {
      return;
    }

    if (!assignment.total) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('ADMIN.FORMS.ASSIGNMENT.NO_CLINICS_SUMMARY'),
        detail: this.translate.instant('ADMIN.FORMS.ASSIGNMENT.NO_CLINICS_DETAIL'),
      });
      return;
    }

    if (assignment.failed === 0) {
      this.messageService.add({
        severity: 'success',
        summary: this.translate.instant('ADMIN.FORMS.ASSIGNMENT.SUCCESS_SUMMARY'),
        detail: this.translate.instant('ADMIN.FORMS.ASSIGNMENT.SUCCESS_DETAIL', {
          success: assignment.success,
          total: assignment.total,
        }),
      });
      return;
    }

    this.messageService.add({
      severity: 'warn',
      summary: this.translate.instant('ADMIN.FORMS.ASSIGNMENT.PARTIAL_SUMMARY'),
      detail: this.translate.instant('ADMIN.FORMS.ASSIGNMENT.PARTIAL_DETAIL', {
        success: assignment.success,
        total: assignment.total,
      }),
    });
  }

  private normalizeColor(value: unknown): string {
    if (value && typeof value === 'object') {
      const color = value as { r?: number; g?: number; b?: number };
      if (
        Number.isFinite(color.r) &&
        Number.isFinite(color.g) &&
        Number.isFinite(color.b)
      ) {
        const toHex = (channel: number) =>
          Math.max(0, Math.min(255, Math.round(channel)))
            .toString(16)
            .padStart(2, '0');
        return `#${toHex(color.r as number)}${toHex(color.g as number)}${toHex(color.b as number)}`;
      }
    }

    const normalized = `${value || ''}`.trim().replace('#', '');
    if (/^[0-9a-fA-F]{3}$/.test(normalized) || /^[0-9a-fA-F]{6}$/.test(normalized)) {
      return `#${normalized}`;
    }
    return '#3b82f6';
  }
}
